import { useState, useCallback, useEffect } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@shadcn-in-nx/ui/card";
import { Button } from "@shadcn-in-nx/ui/button";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import OutcomeRow from "./OutcomeRow";

export default function OutcomesTab() {
    const { t } = useTranslation();
    const [outcomes, setOutcomes] = useState([]);
    const [stats, setStats] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [filterOutcome, setFilterOutcome] = useState("");

    const { call: fetchOutcomes, isLoading } = useBluelagoon({ method: "getIntelligenceOutcomes" });
    const { call: fetchStats } = useBluelagoon({ method: "getIntelligenceOutcomeStats" });

    const load = useCallback(async () => {
        const params = { page, limit: 20 };
        if (filterOutcome) params.outcome = filterOutcome;
        const [outRes, statsRes] = await Promise.all([
            fetchOutcomes(params),
            page === 1 && !filterOutcome ? fetchStats() : Promise.resolve(null),
        ]);
        if (outRes?.data) {
            setOutcomes(outRes.data.outcomes || []);
            setTotalPages(outRes.data.totalPages || 1);
            setTotal(outRes.data.total || 0);
        }
        if (statsRes?.data) setStats(statsRes.data);
    }, [fetchOutcomes, fetchStats, page, filterOutcome]);

    useEffect(() => { load(); }, [load]);

    const handleFilter = useCallback((val) => {
        setFilterOutcome((prev) => (prev === val ? "" : val));
        setPage(1);
    }, []);

    return (
        <div className="space-y-4 mt-4">
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard label={t("admin.intelligence.outcomes.total")} value={stats.totalOutcomes} />
                    <StatCard label={t("admin.intelligence.outcomes.acceptanceRate")} value={`${stats.acceptanceRate}%`} />
                    <StatCard label={t("admin.intelligence.outcomes.avgIterations")} value={stats.avgIterations} />
                </div>
            )}
            <div className="flex gap-2 flex-wrap">
                {["accepted", "rejected_reprompt", "rejected_rollback", "abandoned"].map((val) => (
                    <Button key={val} size="sm" variant={filterOutcome === val ? "default" : "outline"}
                        onClick={() => handleFilter(val)}>
                        {val}
                    </Button>
                ))}
                {filterOutcome && (
                    <Button size="sm" variant="ghost" onClick={() => { setFilterOutcome(""); setPage(1); }}>
                        {t("common.clear")}
                    </Button>
                )}
            </div>
            {isLoading && !outcomes.length ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            ) : (
                <div className="space-y-2">
                    {outcomes.map((o) => <OutcomeRow key={o.id} outcome={o} />)}
                    {outcomes.length === 0 && (
                        <p className="text-center text-muted-foreground py-8">
                            {t("admin.intelligence.outcomes.noOutcomes")}
                        </p>
                    )}
                </div>
            )}
            {totalPages > 1 && (
                <div className="flex items-center justify-between pt-2">
                    <span className="text-sm text-muted-foreground">
                        {t("admin.intelligence.outcomes.showing", { total })}
                    </span>
                    <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm">{page} / {totalPages}</span>
                        <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatCard({ label, value }) {
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
            </CardHeader>
            <CardContent><p className="text-2xl font-bold">{value}</p></CardContent>
        </Card>
    );
}
